import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import SelectOnPath from '../../image/SelectOn.png';
import SelectOffPath from '../../image/SelectOff.png';
import { ReactComponent as CheckIcon } from '../../image/Check.svg';

const SignUpSelectBox = forwardRef((props, ref) => {
	const { t } = useLanguage();
	const { isDark } = useTheme();
	const theme = isDark ? darkTheme : lightTheme;

	const [isClicked, setIsClicked] = useState(false);
	const [defaultVal, setDefaultVal] = useState({ value: 'crossfit', text: '' });

	const SelectRef = useRef();

	const options = [
		{ value: 'crossfit', text: t('signUp.centerTypes.crossfit') },
		{ value: 'hyrox', text: t('signUp.centerTypes.hyrox') },
		{ value: 'crossTraining', text: t('signUp.centerTypes.crossTraining') },
		{ value: 'pilates', text: t('signUp.centerTypes.pilates') },
		{ value: 'pt', text: t('signUp.centerTypes.pt') },
		{ value: 'groupPt', text: t('signUp.centerTypes.groupPt') },
		{ value: 'bodyCorrection', text: t('signUp.centerTypes.bodyCorrection') },
		{ value: 'weightTraining', text: t('signUp.centerTypes.weightTraining') },
		{ value: 'bodybuilding', text: t('signUp.centerTypes.bodybuilding') },
		{ value: 'dietProgram', text: t('signUp.centerTypes.dietProgram') },
		{ value: 'bodyProfile', text: t('signUp.centerTypes.bodyProfile') },
		{ value: 'womensFitness', text: t('signUp.centerTypes.womensFitness') },
		{ value: 'seniorFitness', text: t('signUp.centerTypes.seniorFitness') },
		{ value: 'kidsFitness', text: t('signUp.centerTypes.kidsFitness') },
		{ value: 'gxProgram', text: t('signUp.centerTypes.gxProgram') },
		{ value: 'functionalTraining', text: t('signUp.centerTypes.functionalTraining') },
		{ value: 'personalTraining', text: t('signUp.centerTypes.personalTraining') },
		{ value: 'rehabTraining', text: t('signUp.centerTypes.rehabTraining') },
		{ value: 'sportsPerformance', text: t('signUp.centerTypes.sportsPerformance') },
		{ value: 'powerlifting', text: t('signUp.centerTypes.powerlifting') },
		{ value: 'flyingYoga', text: t('signUp.centerTypes.flyingYoga') },
		{ value: 'vinyasaYoga', text: t('signUp.centerTypes.vinyasaYoga') },
		{ value: 'cyclingClass', text: t('signUp.centerTypes.cyclingClass') },
		{ value: 'muaythaiFitness', text: t('signUp.centerTypes.muaythaiFitness') },
		{ value: 'mmaFitness', text: t('signUp.centerTypes.mmaFitness') },
		{ value: 'jiujitsuConditioning', text: t('signUp.centerTypes.jiujitsuConditioning') },
		{ value: 'runningClass', text: t('signUp.centerTypes.runningClass') },
		{ value: 'marathonTraining', text: t('signUp.centerTypes.marathonTraining') },
		{ value: 'hiitTraining', text: t('signUp.centerTypes.hiitTraining') },
		{ value: 'circuitTraining', text: t('signUp.centerTypes.circuitTraining') },
		{ value: 'coreTraining', text: t('signUp.centerTypes.coreTraining') },
		{ value: 'kettlebellTraining', text: t('signUp.centerTypes.kettlebellTraining') },
		{ value: 'stretchingClass', text: t('signUp.centerTypes.stretchingClass') },
		{ value: 'mobilityClass', text: t('signUp.centerTypes.mobilityClass') },
		{ value: 'calisthenics', text: t('signUp.centerTypes.calisthenics') },
		{ value: 'strongmanTraining', text: t('signUp.centerTypes.strongmanTraining') },
		{ value: 'emsTraining', text: t('signUp.centerTypes.emsTraining') },
		{ value: 'others', text: t('signUp.centerTypes.others') },
	];

	// 언어 변경 시 기본값 갱신 //
	useEffect(() => {
		setDefaultVal({ value: 'crossfit', text: t('signUp.centerTypes.crossfit') });
	}, [t]);

	// SelectBox 바깥 클릭 시 닫기 //
	useEffect(() => {
		function handleOutsideClick(e) {
			if (!isClicked) return;

			if (SelectRef.current && !SelectRef.current.contains(e.target)) {
				SelectRef.current.style.border = `1px solid ${theme.gray}`;
				SelectRef.current.style.height = '34px';
				SelectRef.current.style.overflow = 'hidden';
				setIsClicked(false);
			}
		}

		document.addEventListener('mousedown', handleOutsideClick);

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [isClicked]);

	useImperativeHandle(ref, () => ({
		getValue: () => {
			return defaultVal.value;
		}
	}))

	// SelectBox 열기/닫기 //
	function onClickSelectBox() {
		if (isClicked) {
			SelectRef.current.style.border = `1px solid ${theme.gray}`;
			SelectRef.current.style.height = `40px`;
			SelectRef.current.style.overflow = 'hidden';
			setIsClicked(false);
		}
		else {
			SelectRef.current.style.border = `1px solid ${theme.primary}`;
			SelectRef.current.style.height = `240px`;
			SelectRef.current.style.overflowY = 'scroll';
			SelectRef.current.style.overflowX = 'none';
			setIsClicked(true);
		}
	}

	// 옵션 선택 //
	function onClickOption(index) {
		setDefaultVal(options[index]);
	}

	return (
		<TopLevelWrapper>
			<SelectBox
				ref={SelectRef}
				isClicked={isClicked}
				buttonPath={isClicked ? SelectOnPath : SelectOffPath}
				onClick={onClickSelectBox}>
				{
					isClicked ?
						options.map((data, index) =>
							<OptionWrapper
								key={'CenterType-' + index}
								onClick={() => onClickOption(index)}>
								<Option $isSelected={data.value === defaultVal.value && data.text === defaultVal.text}>{data.text}</Option>
								{
									data.value === defaultVal.value && data.text === defaultVal.text &&
									<OptionCheckIcon>
										<CheckIcon />
									</OptionCheckIcon>
								}
							</OptionWrapper>
						)
						:
						<OptionWrapper>
							<Option>{defaultVal.text}</Option>
						</OptionWrapper>
				}
			</SelectBox>
		</TopLevelWrapper>
	)
})

export default SignUpSelectBox;

const TopLevelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	
	width: 200px;
	height: 34px;
	// height: max(32px, 1.979166666666667vw);

	margin-top: 10px;
	// margin-top: max(12.27px, 0.6770833333333333vw);
`;

const SelectBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: ${props => props.isClicked ? 'flex-start' : 'center'};
	align-items: flex-start;

	width: 200px;
	height: 34px;
	// height: max(32px, 1.979166666666667vw);

	border: 1px solid ${props => props.theme.line};
	border-radius: 8px;
	overflow: hidden;

	z-index: 2;

  background: url(${props => props.buttonPath}) no-repeat 96% ${props => props.isClicked ? `3` : `55`}%;
	background-color: ${props => props.theme.subBackground};
  transition: all 0.3s ease-in-out;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
		}
	}

	&::-webkit-scrollbar {
		width: 9px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.scrollBar};
		border: 3px solid transparent;
		height: 25px;
		// background-color: #FF8B48;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: space-between;
	align-items: center;

	width: 100%;
	box-sizing: border-box;

	padding: 0 13px;

	background-color: transparent;

	transition: background-color 0.15s ease-in-out;

	-webkit-tap-highlight-color: transparent;

	@media (hover: hover) {
		&:hover {
			cursor: pointer;
			background-color: ${props => props.theme.primary};
		}

		&:hover p {
			color: ${props => props.theme.white};
		}

		&:hover svg {
			color: ${props => props.theme.white};
		}
	}

	&:active {
		background-color: ${props => props.theme.primary};
	}

	&:active p {
		color: ${props => props.theme.white};
	}

	&:active svg {
		color: ${props => props.theme.white};
	}
`;

const Option = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 16px;

	color: ${props => props.$isSelected ? props.theme.primary : props.theme.text};

	margin: 10px 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

	transition: color 0.15s ease-in-out;
`;

const OptionCheckIcon = styled.div`
	display: flex;
	flex-direction: column;

	justify-content: center;
	align-items: center;

	flex-shrink: 0;

	color: ${props => props.theme.primary};

	& svg {
		width: 12px;
		height: 9px;
	}
`;